/* eslint-disable max-lines */
import { get } from 'svelte/store';
import { getOptions } from '../../../common/api.js';
import { STATE, CAMPAIGN_TOKEN } from '../../../stores/state.js';
import { addLoader, removeLoader } from '../../../stores/loading.js';
import { addMessage } from '../../../stores/messages.js';
import * as PEGASUS from '@smatch/pegasus-api-docs';
import { SESSION } from './store.js';
import { ADDRESS_STORE } from '../../address/index.js';
import { SUBSCRIPTION_STORE } from '../../subscription/index.js';
import { USER_STORE } from '../../user/index.js';
const onAuthentication = (session) => {
    if (!('_' in session)) {
        return;
    }
    // State must be updated before session b/c the API token is stored in STATE (and session updates may trigger API requests)
    STATE.update(state => ({
        ...state,
        currencyCode: session.user.locale.currencyCode,
        hasAuthenticated: session.user._.state !== 'anonymous',
        languageCode: session.user.locale.languageCode,
        token: session.token,
    }));
    SESSION.update(() => session);
};
export const clearSession = () => {
    const TOKEN = get(SESSION)?.token;
    // We need to use a generic truthy check b/c the token may be gone for a variety of reasons
    if (Boolean(TOKEN)) {
        void PEGASUS.logoutSession({ ...getOptions(), token: TOKEN });
    }
    SESSION.update(() => null);
    STATE.update(state => ({
        ...state,
        token: null,
    }));
    ADDRESS_STORE.update(() => ({}));
    USER_STORE.update(() => ({}));
    SUBSCRIPTION_STORE.update(() => ({}));
};
const requestWithBody = async (request, body, options = {}) => {
    const OPTIONS = getOptions();
    const KEY = addLoader();
    const RESPONSE = await request(body, { ...OPTIONS, ...options });
    removeLoader(KEY);
    return {
        data: RESPONSE.data,
        statusCode: RESPONSE.statusCode,
    };
};
const requestWithoutBody = async (request, options = {}) => {
    const OPTIONS = getOptions();
    const KEY = addLoader();
    const RESPONSE = await request({ ...OPTIONS, ...options });
    removeLoader(KEY);
    return {
        data: RESPONSE.data,
        statusCode: RESPONSE.statusCode,
    };
};
const defaultErrorHandler = (statusCode) => {
    switch (statusCode) {
        case 'STATUS_429':
            return { error: 'global.errors.too_many_requests.description' };
        case 'STATUS_503':
            return { error: 'global.errors.maintenance.description' };
        default:
            return { error: 'global.errors.general_error.full' };
    }
};
// eslint-disable-next-line max-lines-per-function
export const login = async (body) => {
    const OPTIONS = { headers: {} };
    if (get(CAMPAIGN_TOKEN) !== undefined) {
        OPTIONS.headers = { 'x-campaign-token': get(CAMPAIGN_TOKEN) };
    }
    const { statusCode: STATUS_CODE, data: DATA } = await requestWithBody(PEGASUS.loginSession, body, OPTIONS);
    if ('token' in DATA) {
        // @ts-expect-error - TODO: Fix Session Type
        onAuthentication(DATA);
        STATE.update(state => ({ ...state, campaignToken: null }));
        return DATA;
    }
    switch (STATUS_CODE) {
        case 'STATUS_404':
            return { error: 'component.session.login.error.credentials_invalid' };
        case 'STATUS_410':
            return { error: 'component.session.login.error.user_gone' };
        case 'STATUS_412':
            return { error: 'component.session.login.error.organization_gone' };
        default:
            return defaultErrorHandler(STATUS_CODE);
    }
};
// eslint-disable-next-line max-len
export const fetchAuthenticationTypes = async (body) => {
    const { statusCode: STATUS_CODE, data: DATA } = await requestWithBody(PEGASUS.fetchAuthenticationTypes, body);
    if (Array.isArray(DATA)) {
        return DATA;
    }
    switch (STATUS_CODE) {
        case 'STATUS_403':
            return ['blocked'];
        case 'STATUS_404':
            return ['unknown'];
        default:
            return defaultErrorHandler(STATUS_CODE);
    }
};
// eslint-disable-next-line max-lines-per-function
export const sendAuthenticationUrl = async (body) => {
    const OPTIONS = { headers: {} };
    if (get(CAMPAIGN_TOKEN) !== undefined) {
        OPTIONS.headers = { 'x-Campaign-token': get(CAMPAIGN_TOKEN) };
    }
    const { statusCode: STATUS_CODE } = await requestWithBody(PEGASUS.sendAuthenticationUrl, body, OPTIONS);
    switch (STATUS_CODE) {
        case 'STATUS_202':
            return {};
        case 'STATUS_404':
            return { error: 'component.session.send_one_time_link.error.no_user' };
        case 'STATUS_410':
            return { error: 'component.session.send_one_time_link.error.user_gone' };
        case 'STATUS_412':
            return { error: 'component.session.send_one_time_link.error.organization_gone' };
        default:
            return defaultErrorHandler(STATUS_CODE);
    }
};
export const sendEmailConfirmationUrl = async (body) => {
    const { statusCode: STATUS_CODE } = await requestWithBody(PEGASUS.sendEmailConfirmationUrl, body);
    switch (STATUS_CODE) {
        case 'STATUS_202':
            return {};
        case 'STATUS_404':
            return { error: 'component.session.send_email_confirmation_url.error.no_user' };
        case 'STATUS_410':
            return { error: 'component.session.send_email_confirmation_url.error.user_gone' };
        case 'STATUS_412':
            return { error: 'component.session.send_email_confirmation_url.error.organization_gone' };
        default:
            return defaultErrorHandler(STATUS_CODE);
    }
};
// eslint-disable-next-line max-lines-per-function
export const register = async (body) => {
    const OPTIONS = { headers: {} };
    if (get(CAMPAIGN_TOKEN) !== undefined) {
        OPTIONS.headers = { 'x-Campaign-token': get(CAMPAIGN_TOKEN) };
    }
    const { statusCode: STATUS_CODE, data: DATA } = await requestWithBody(PEGASUS.registerSession, body, OPTIONS);
    if ('token' in DATA) {
        // @ts-expect-error - TODO: Fix Session Type
        onAuthentication(DATA);
        STATE.update(state => ({ ...state, campaignToken: null }));
        return DATA;
    }
    switch (STATUS_CODE) {
        case 'STATUS_409':
            return { error: 'component.session.registration.error.email_conflict' };
        case 'STATUS_429':
            return { error: 'global.errors.too_many_requests.description' };
        case 'STATUS_503':
            return { error: 'global.errors.maintenance.description' };
        default:
            return { error: 'global.errors.general_error.full' };
    }
};
// eslint-disable-next-line max-lines-per-function
export const sendPasswordUpdateUrl = async (body) => {
    const OPTIONS = { headers: {} };
    if (get(CAMPAIGN_TOKEN) !== undefined) {
        OPTIONS.headers = { 'x-Campaign-token': get(CAMPAIGN_TOKEN) };
    }
    const { statusCode: STATUS_CODE } = await requestWithBody(PEGASUS.sendPasswordUpdateUrl, body, OPTIONS);
    switch (STATUS_CODE) {
        case 'STATUS_202':
            return {};
        case 'STATUS_404':
            return { error: 'component.session.forgot_password.error.no_user' };
        case 'STATUS_410':
            return { error: 'component.session.forgot_password.error.user_gone' };
        case 'STATUS_412':
            return { error: 'component.session.forgot_password.error.organization_gone' };
        default:
            return defaultErrorHandler(STATUS_CODE);
    }
};
// eslint-disable-next-line max-lines-per-function, max-len
export const updatePasswordViaToken = async (body, token) => {
    const { statusCode: STATUS_CODE, data: DATA } = await requestWithBody(PEGASUS.updatePasswordViaToken, body, { token });
    if ('token' in DATA) {
        // @ts-expect-error - TODO: Fix Session Type
        onAuthentication(DATA);
        STATE.update(state => ({ ...state, campaignToken: null }));
        return DATA;
    }
    switch (STATUS_CODE) {
        case 'STATUS_401':
            clearSession();
            return { error: 'component.session.update_password.error.token_invalid' };
        case 'STATUS_404':
            clearSession();
            return { error: 'component.session.update_password.error.no_user' };
        case 'STATUS_410':
            clearSession();
            return { error: 'component.session.update_password.error.user_gone' };
        case 'STATUS_412':
            clearSession();
            return { error: 'component.session.update_password.error.organization_gone' };
        default:
            return defaultErrorHandler(STATUS_CODE);
    }
};
// Authenticate (via Token)
// eslint-disable-next-line max-lines-per-function, max-len
export const authenticateViaToken = async (token) => {
    if (!Boolean(token)) {
        return { error: 'component.session.one_time_link.error.token_missing' };
    }
    const { statusCode: STATUS_CODE, data: DATA } = await requestWithoutBody(PEGASUS.authenticateViaToken, { token });
    if ('token' in DATA) {
        // @ts-expect-error - TODO: Fix Session Type
        onAuthentication(DATA);
        STATE.update(state => ({ ...state, campaignToken: null }));
        return DATA;
    }
    switch (STATUS_CODE) {
        case 'STATUS_401':
            clearSession();
            return { error: 'component.session.one_time_link.error.token_invalid' };
        case 'STATUS_404':
            clearSession();
            return { error: 'component.session.one_time_link.error.no_user' };
        case 'STATUS_410':
            clearSession();
            return { error: 'component.session.one_time_link.error.user_gone' };
        case 'STATUS_412':
            clearSession();
            return { error: 'component.session.one_time_link.error.organization_gone' };
        default:
            return defaultErrorHandler(STATUS_CODE);
    }
};
// Confirm Email (via Token)
// eslint-disable-next-line max-lines-per-function
export const confirmEmailViaToken = async (token) => {
    const { statusCode: STATUS_CODE, data: DATA } = await requestWithoutBody(PEGASUS.confirmEmailViaToken, { token });
    if ('token' in DATA) {
        // @ts-expect-error - TODO: Fix Session Type
        onAuthentication(DATA);
        return DATA;
    }
    switch (STATUS_CODE) {
        case 'STATUS_401':
            clearSession();
            return { error: 'component.session.confirm_email.error.token_invalid' };
        case 'STATUS_404':
            clearSession();
            return { error: 'component.session.confirm_email.error.no_user' };
        case 'STATUS_410':
            clearSession();
            return { error: 'component.session.confirm_email.error.user_gone' };
        case 'STATUS_412':
            clearSession();
            return { error: 'component.session.confirm_email.error.organization_gone' };
        default:
            return defaultErrorHandler(STATUS_CODE);
    }
};
// Fetch Session
// eslint-disable-next-line max-len, max-lines-per-function
export const fetchSession = async (token = null) => {
    if (token === null) {
        return null;
    }
    const { statusCode: STATUS_CODE, data: DATA } = await requestWithoutBody(PEGASUS.fetchSession, { token });
    if ('token' in DATA) {
        // @ts-expect-error - TODO: Fix Session Type
        onAuthentication(DATA);
        return DATA;
    }
    switch (STATUS_CODE) {
        case 'STATUS_401':
        case 'STATUS_404':
            clearSession();
            if (window.location.pathname !== '/') {
                addMessage({
                    heading: 'global.errors.session_expired',
                    type: 'warning',
                });
                window.location.href = window.location.origin;
            }
            break;
        default:
            addMessage({
                description: 'global.errors.general_error.description',
                heading: 'global.errors.general_error.heading',
                type: 'error',
            });
    }
    return null;
};
export const sync = async ({ userKey, organizationKey }) => {
    const SESSION_CACHE = get(SESSION);
    if (SESSION_CACHE === null) {
        return;
    }
    if (userKey === undefined && organizationKey === undefined) {
        return;
    }
    if (userKey !== undefined && userKey !== SESSION_CACHE.user._.key) {
        return;
    }
    if (organizationKey !== undefined && organizationKey !== SESSION_CACHE.organization._.key) {
        return;
    }
    await fetchSession(SESSION_CACHE.token);
};
